import {
    ChangeDetectionStrategy,
    Component,
    Input,
    OnDestroy,
    OnInit,
    TemplateRef,
    ViewChild
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatLegacyFormFieldModule as MatFormFieldModule } from '@angular/material/legacy-form-field';
import { MatLegacyInputModule as MatInputModule } from '@angular/material/legacy-input';
import { MatLegacyMenuModule as MatMenuModule } from '@angular/material/legacy-menu';
import { MatLegacyOptionModule as MatOptionModule } from '@angular/material/legacy-core';
import { MatLegacyPaginatorModule as MatPaginatorModule } from '@angular/material/legacy-paginator';
import { PageEvent } from '@angular/material/paginator';
import { MatLegacySelectModule as MatSelectModule } from '@angular/material/legacy-select';
import { FormBuilder, FormControl, ReactiveFormsModule } from '@angular/forms';
import { SbAnimatedNumberComponent } from '../../../../../shared/components/sb-animated-number/sb-animated-number.component';
import {
    BadgesChangeEvent,
    SbBadgesComponent
} from '../../../../../shared/components/sb-badges/sb-badges.component';
import { SbCompanyLogoComponent } from '../../../../../shared/components/sb-company-logo/sb-company-logo.component';
import { SbFilterCountPipe } from '../../../../../shared/pipes/sb-filter-count.pipe';
import { SbGenericTypeSelectComponent } from '../../../accounting/components/sb-generic-type-select/sb-generic-type-select.component';
import { SbNotificationBadgeComponent } from '../../../../../shared/components/sb-notification-badge/sb-notification-badge.component';
import { SharedModule } from '../../../../../shared/shared.module';
import { TablerIconsModule } from 'angular-tabler-icons';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import {
    BehaviorSubject,
    combineLatest,
    defer,
    Observable,
    of,
    Subject,
    switchMap
} from 'rxjs';
import { AcctsService } from '../../../../../shared/state/accts/accts.service';
import { AcctsQuery } from '../../../../../shared/state/accts/accts.query';
import { AccountIssuersQuery } from '../../../../admin/state/account-issuers/account-issuers.query';
import { InstrumentsQuery } from '../../../../../shared/state/instruments/instruments.query';
import { LegalEntityQuery } from '../../../../admin/state/legal-entities/legal-entity.query';
import { PositionsQuery } from '../../../../../features/positions/state/positions.query';
import { PositionsService } from '../../../../../features/positions/state/positions.service';
import { UserSettingsQuery } from '../../../../../features/user-settings/state/user-settings.query';
import { UserSettingsService } from '../../../../../features/user-settings/state/user-settings.service';
import { TagsQuery } from '../../../../../features/new-tags/state/tags.query';
import { ActivatedRoute, Router } from '@angular/router';
import { RouterQuery } from '@datorama/akita-ng-router-store';
import { UiQuery } from '../../../../../shared/state/ui/ui.query';
import {
    debounceTime,
    distinctUntilChanged,
    filter,
    map,
    shareReplay,
    startWith,
    take,
    takeUntil,
    tap,
    throttleTime,
    withLatestFrom
} from 'rxjs/operators';
import { AccountTypeEnum, Acct } from '../../../../../shared/state/accts/acct.model';
import { LegalEntity } from '../../../../admin/state/legal-entities/legal-entity.model';
import {
    combineQueries,
    distinctUntilArrayItemChanged,
    filterNilValue,
    HashMap
} from '@datorama/akita';
import { AccountIssuer } from '../../../../admin/state/account-issuers/account-issuer.model';
import { Instrument } from '../../../../../shared/state/instruments/instrument.model';
import { Tag } from '../../../../../features/new-tags/state/tags.model';
import { InferFilter } from '../../../../../shared/state/query-filter.functions';
import { ComponentSettings } from '../../../../../features/user-settings/state/user-setting.model';
import {
    fadeInOut,
    fadeInOutModal,
    list,
    slideInOut
} from '../../../../../shared/animations/fade-in-out.animation';
import { Position } from '../../../../../features/positions/state/position.model';
import { SettingsQuery } from '../../../../../features/settings/state/settings.query';
import capitalize from 'lodash/capitalize';
import { accumulateByGroup } from '../../../../../features/aggregation/aggregation';
import { MatLegacyTooltipModule as MatTooltipModule } from '@angular/material/legacy-tooltip';
import { AccountLogoPipe } from '../../../../../shared/pipes/account-logo.pipe';
import { EntityConcatPipe } from './entity-concat.pipe';
import { CountPipe } from '../../../tags/pipes/count.pipe';
import { LegalEntityLogoPipe } from '../../../../../shared/pipes/legal-entity-logo.pipe';
import { SbGenericModalComponent } from '../../../../../shared/components/sb-generic-modal/sb-generic-modal.component';
import { ModalTemplateOptions } from '../../../../../shared/components/sb-generic-modal/sb-generic-modal.service';
import { get } from 'dot-prop';
import { ToastService } from '../../../../../shared/services/toast/toast.service';
export interface ExtendedPosition extends Position {
    acquisitionValueInBaseCurrency: number;
    totalPnlInBaseCurrency: number;
    issuer: AccountIssuer;
    legalEntity: LegalEntity;
    legalEntityId: LegalEntity['ownerId'];
    issuerId: AccountIssuer['accountIssuerId'];
    accountId: Acct['accountId'];
    instrument: Instrument;
    assetType: Instrument['assetType'];
    assetClass: Instrument['assetClass'] | 'ERROR_MISSING_INSTRUMENT';
}
@Component({
    selector: 'sb-balance-list',
    standalone: true,
    imports: [
        CommonModule,
        MatFormFieldModule,
        MatInputModule,
        MatMenuModule,
        MatOptionModule,
        MatPaginatorModule,
        MatSelectModule,
        ReactiveFormsModule,
        SbAnimatedNumberComponent,
        SbBadgesComponent,
        SbCompanyLogoComponent,
        SbFilterCountPipe,
        SbGenericTypeSelectComponent,
        SbNotificationBadgeComponent,
        SharedModule,
        TablerIconsModule,
        TranslateModule,
        MatTooltipModule,
        AccountLogoPipe,
        EntityConcatPipe,
        CountPipe,
        LegalEntityLogoPipe,
        SbGenericModalComponent
    ],
    templateUrl: './sb-balance-list.component.html',
    styleUrls: ['./sb-balance-list.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    animations: [list, slideInOut, fadeInOut, fadeInOutModal]
})
export class SbBalanceListComponent implements OnInit, OnDestroy {
    get isLoading$(): Observable<boolean> {
        return this._isLoading$.asObservable();
    }

    set isLoading(value: boolean) {
        this._isLoading$.next(value);
    }
    get isStandalone(): boolean {
        return this._isStandalone;
    }

    @Input() set isStandalone(value: boolean) {
        this._isStandalone = value;
    }
    get useShortNumberFormat(): boolean {
        return this._useShortNumberFormat;
    }

    set useShortNumberFormat(value: boolean) {
        this._useShortNumberFormat = value;
    }
    get balanceKey$(): Observable<string> {
        return this._balanceKey$.asObservable();
    }

    get balanceKey(): string {
        return this._balanceKey$.getValue();
    }

    set balanceKey(value: string) {
        this._balanceKey$.next(value);
    }
    _unsubscribe$ = new Subject();
    get currentPage$(): Observable<number> {
        return this._currentPage$
            .asObservable()
            .pipe(distinctUntilChanged(), shareReplay(1));
    }

    set currentPage(value: number) {
        this._currentPage$.next(value);
    }
    get showFilters(): boolean {
        return this._showFilters;
    }

    set showFilters(value: boolean) {
        this._showFilters = value;
    }
    constructor(
        private _accountsService: AcctsService,
        private _accountsQuery: AcctsQuery,
        private _accountIssuersQuery: AccountIssuersQuery,
        private _instrumentsQuery: InstrumentsQuery,
        private _legalEntityQuery: LegalEntityQuery,
        private _positionsQuery: PositionsQuery,
        private _positionsService: PositionsService,
        private _userSettingsQuery: UserSettingsQuery,
        private _userSettingsService: UserSettingsService,
        private _settingsQuery: SettingsQuery,
        private _translateService: TranslateService,
        private _tagsQuery: TagsQuery,
        private _fb: FormBuilder,
        private _router: Router,
        private _route: ActivatedRoute,
        private _routerQuery: RouterQuery,
        private _uiQuery: UiQuery,
        private _toastService: ToastService
    ) {}

    viewPortSize$ = this._uiQuery.viewportSize$;
    scrollPosition$ = this._uiQuery.scrollYPositionObservable$.pipe(
        switchMap((observable) => observable),
        throttleTime(100, undefined, { leading: true, trailing: true }),
        shareReplay(1)
    );

    ngOnInit() {
        combineLatest([
            this._userSettingsQuery.selectComponentSetting$(
                'ACCOUNT_TYPE_FILTER',
                'sb-account-list'
            ),
            this._accountsQuery.selectAvailableAccountTypes$.pipe(
                filter((types) => types.length > 0)
            )
        ])
            .pipe(
                //debounceTime(0),
                tap(([persisted, available]) => {
                    if (persisted && persisted.length > 0) {
                        this.filterForm.controls.type.setValue(persisted);
                    } else {
                        this.filterForm.controls.type.setValue(available);
                    }
                }),
                take(1),
                takeUntil(this._unsubscribe$)
            )
            .subscribe();

        this.availableLegalEntities$
            .pipe(
                tap((available) => {
                    this.legalEntitiesCtrl.setValue(
                        available.map((legalEntity) => legalEntity.data.ownerId)
                    );
                }),
                take(1),
                takeUntil(this._unsubscribe$)
            )
            .subscribe();

        combineLatest([
            this._userSettingsQuery.selectComponentSetting$(
                'ISSUER_FILTER',
                'sb-account-list'
            ),
            this.availableIssuers$.pipe(filter((types) => types.length > 0))
        ])
            .pipe(
                tap(([persisted, available]) => {
                    if (persisted && persisted.length > 0) {
                        this.issuersCtrl.setValue(persisted);
                    } else {
                        this.issuersCtrl.setValue(
                            available.map((issuer) => issuer.data.accountIssuerId)
                        );
                    }
                }),
                take(1),
                takeUntil(this._unsubscribe$)
            )
            .subscribe();

        combineLatest([
            this._userSettingsQuery.selectComponentSetting$(
                'INSTRUMENT_FILTER',
                'sb-account-list'
            ),
            this.availableInstruments$.pipe(filter((types) => types.length > 0))
        ])
            .pipe(
                tap(([persisted, available]) => {
                    if (persisted && persisted.length > 0) {
                        this.instrumentsCtrl.setValue(persisted);
                    } else {
                        this.instrumentsCtrl.setValue(
                            available.reduce((acc, instrumentBadge) => {
                                return [...acc, ...instrumentBadge.data];
                            }, [])
                        );
                    }
                }),
                take(1),
                takeUntil(this._unsubscribe$)
            )
            .subscribe();

        /*combineLatest([
            this._accountsQuery
                .selectAccountsByFilter$({
                    accountType: {
                        ne: AccountTypeEnum.BROKER
                    }
                })
                .pipe(
                    filter((accounts) => accounts.length > 0),
                    take(1),
                    map((accounts) => accounts.map((account) => account.accountId))
                ),
            this.baseCurrency$
        ])
            .pipe(
                switchMap(([accountIds, baseCurrency]) => {
                    this.loadingBalance$.next(true);
                    return this._positionsService
                        .get({ accountIds, includeNetted: true, baseCurrency })
                        .pipe(tap((_) => this.loadingBalance$.next(false)));
                }),
                takeUntil(this._unsubscribe$)
            )
            .subscribe();*/

        this._accountsQuery.selectAccountTags$
            .pipe(
                tap((tags) => {
                    this.filterForm.controls.tags.setValue(tags);
                }),
                takeUntil(this._unsubscribe$)
            )
            .subscribe();

        this.loadActiveDimension();
    }

    baseCurrency$ = this._userSettingsQuery.selectGlobalSetting$('BASE_CURRENCY');
    maxAccountCloseDate$ = of(new Date());

    private _currentPage$ = new BehaviorSubject(1);

    legalEntitiesCtrl = new FormControl<Array<LegalEntity['ownerId']>>([]);
    activeLegalEntityIds$ = defer(() =>
        this._legalEntityQuery.active$.pipe(
            map((legalEntities) =>
                legalEntities.map((legalEntity) => legalEntity.ownerId)
            ),
            distinctUntilArrayItemChanged(),
            tap((_) => {
                this.currentPage = 1;
            }),
            shareReplay(1)
        )
    ); /*this.legalEntitiesCtrl.valueChanges.pipe(
        debounceTime(300),
        tap((_) => {
            this.currentPage = 1;
        }),
        startWith(this.legalEntitiesCtrl.value),
        distinctUntilArrayItemChanged(),
        shareReplay(1)
    );*/

    issuersCtrl = new FormControl<Array<AccountIssuer['accountIssuerId']>>([]);
    activeIssuerIds$ = defer(() =>
        this.issuersCtrl.valueChanges.pipe(
            tap((_) => {
                this.currentPage = 1;
            }),
            startWith(this.issuersCtrl.value),
            distinctUntilArrayItemChanged(),
            shareReplay(1)
        )
    );

    availableIssuers$ = combineLatest([
        this._accountsQuery.selectAvailableAccountIssuers$,
        this._accountIssuersQuery.allAsObject$.pipe(
            filter((issuers) => Object.keys(issuers).length > 0)
        ),
        this.activeIssuerIds$
    ]).pipe(
        map(([issuerIds, issuers, activeIssuerIds]) => {
            const currentSelection = [...this.issuersCtrl.value];
            return issuerIds.map((id) => {
                // if issuer is not found in the list of issuers then use a default issuer object
                const issuer = issuers[id] || {
                    displayName: 'Unknown',
                    accountIssuerShortName: 'Unknown',
                    accountIssuerLongName: 'Unknown',
                    accountIssuerId: null
                };
                return {
                    title:
                        issuer.displayName ||
                        issuer.accountIssuerShortName ||
                        issuer.accountIssuerLongName,
                    outline:
                        currentSelection.length === 0
                            ? false
                            : !currentSelection.includes(issuer.accountIssuerId),
                    data: issuer
                };
            });
        })
    );

    instrumentsCtrl = new FormControl<Array<Instrument['instrumentId']>>([]);
    activeInstrumentIds$ = defer(() =>
        this.instrumentsCtrl.valueChanges.pipe(
            startWith(this.instrumentsCtrl.value),
            distinctUntilArrayItemChanged(),
            tap((_) => {
                this.currentPage = 1;
            }),
            shareReplay(1)
        )
    );

    availableInstruments$ = combineLatest([
        this._positionsQuery.selectInstrumentIdsWithPositions$(),
        this._instrumentsQuery.allAsObject$.pipe(
            filter((instruments) => Object.keys(instruments).length > 0)
        ),
        this.activeInstrumentIds$
    ]).pipe(
        map(([instrumentIds, instrumentsMap, activeInstrumentIds]) => {
            const currentSelection = [...activeInstrumentIds];
            // each instrument should get a unique tag based on the instrument name, some instrument don't have meaningful names so we use the type as a fallback to group them instead eg. cash instruments.
            return Object.values(
                instrumentIds.reduce(
                    (
                        acc: HashMap<{
                            title: string;
                            outline: boolean;
                            data: Array<Instrument['instrumentId']>;
                        }>,
                        instrumentId
                    ) => {
                        const instrument = instrumentsMap[instrumentId];
                        const name =
                            instrument?.decoratedInstrumentName ||
                            instrument?.name ||
                            instrument?.type ||
                            'Unknown';
                        if (!acc[name]) {
                            acc[name] = {
                                title: name,
                                outline:
                                    activeInstrumentIds.length === 0
                                        ? false
                                        : !currentSelection.includes(
                                              instrument?.instrumentId || instrumentId
                                          ),
                                data: [instrument?.instrumentId || instrumentId]
                            };
                        } else {
                            acc[name].data.push(instrument?.instrumentId || instrumentId);
                        }
                        return acc;
                    },
                    {}
                )
            ).sort((a, b) => a.title.localeCompare(b.title));
        }),
        shareReplay(1)
    );

    availableInstrumentIds$ = this.availableInstruments$.pipe(
        map((instruments) =>
            instruments.reduce((acc, instrument) => [...acc, ...instrument.data], [])
        ),
        shareReplay(1)
    );

    availableLegalEntities$ = combineLatest([
        this._accountsQuery.selectAvailableLegalEntities$,
        this._legalEntityQuery.allAsObject$
    ]).pipe(
        map(([ownerIds, owners]) => {
            const currentSelection = this.legalEntitiesCtrl.value;
            return ownerIds.map((id) => {
                // if legal entity is not found in the list of legal entities then use a default legal entity object
                const legalEntity = owners[id] || {
                    displayName: 'Unknown',
                    shortName: 'Unknown',
                    longName: 'Unknown',
                    ownerId: null
                };
                return {
                    title:
                        legalEntity.displayName ||
                        legalEntity.shortName ||
                        legalEntity.longName,
                    outline: !currentSelection.includes(legalEntity.ownerId),
                    data: legalEntity
                };
            });
        })
    );

    private _showFilters = false;

    sortByOptions = [
        { key: 'default', name: 'Default' },
        { key: 'marketValueInBaseCurrency', name: 'Market value' },
        { key: 'cashInBaseCurrency', name: 'Cash' }
        //{ key: 'name', name: 'Name' },
        //{ key: 'issuer.name', name: 'Issuer' }
    ];
    sortForm = this._fb.group({
        sortBy: [this.sortByOptions[0].key],
        sortByOrder: ['asc']
    });

    sorting$ = this.sortForm.valueChanges.pipe(
        startWith(this.sortForm.value),
        distinctUntilChanged(
            (prev, curr) => JSON.stringify(prev) === JSON.stringify(curr)
        )
    );
    filterForm = this._fb.group({
        type: new FormControl<Array<Acct['accountType']>>([]),
        tags: new FormControl<Array<Tag>>([])
    });
    searchCtrl = new FormControl('');
    searchString$ = this.searchCtrl.valueChanges.pipe(
        debounceTime(300),
        startWith(this.searchCtrl.value)
    );

    private _balanceKey$ = new BehaviorSubject('balanceInBaseCurrency');
    useBaseCurrency$ = this._balanceKey$.pipe(
        map((key) => key === 'balanceInBaseCurrency'),
        distinctUntilChanged(),
        shareReplay(1)
    );

    /** _filteredAccountIds$
     * @description Observable for account ids to filter positions by
     * @returns {Observable<Array<number>}
     */
    private _filteredAccountIds$ = combineQueries([
        this.activeIssuerIds$,
        this.activeLegalEntityIds$
    ]).pipe(
        switchMap(([issuerIds, legalEntityIds]) => {
            // TODO: allow user to change this date?
            const maxAccountCloseDate = new Date();
            const filter: InferFilter<Acct> = {
                accountType: {
                    nin: [AccountTypeEnum.BROKER]
                },
                closingDate: {
                    // hide accounts that have been closed before the maxAccountCloseDate
                    fn: (closingDate: string) => {
                        return closingDate
                            ? new Date(closingDate) > maxAccountCloseDate
                            : true;
                    }
                }
            };

            if (issuerIds.length > 0) {
                filter.accountIssuerId = {
                    in: issuerIds
                };
            }
            // Always filter on legal entities, i.e. if none are selected no positions will be shown
            //if (legalEntityIds.length > 0) {
            filter.ownerId = {
                in: legalEntityIds
            };
            //}
            return this._accountsQuery.selectAccountsByFilter$(filter);
        }),
        map((accounts) => accounts.map((account) => account.accountId)),
        shareReplay(1)
    );

    private _filteredPositions$ = combineQueries([
        this._filteredAccountIds$,
        this.activeInstrumentIds$
    ]).pipe(
        switchMap(([accountIds, instrumentIds]) => {
            const filter: InferFilter<Position> = {
                account: {
                    accountId: {
                        in: accountIds
                    }
                }
            };
            if (instrumentIds.length > 0) {
                filter.instrument = {
                    instrumentId: {
                        in: instrumentIds
                    }
                };
            }
            return this._positionsQuery.selectPositionsByFilter$(filter);
        })
    );

    /** activeLevel$
     * @description Observable for active level, i.e. active element in group with subgroups
     */
    activeLevel$ = this._routerQuery.selectQueryParams('allocationTrail').pipe(
        map((queryParams) => queryParams?.split(',')),
        shareReplay(1)
    );

    /** metricOptions
     * @description Options for metric control, i.e. which metrics to calculate allocations by
     */
    metricOptions = [
        {
            label: 'Market value',
            value: 'marketValueInBaseCurrency',
            key: 'marketValueInBaseCurrency'
        }
    ];

    metricCtrl = new FormControl(
        this._route.snapshot.queryParamMap.get('metric')
            ? this.metricOptions.find(
                  (option) =>
                      option.value.replace(/InBaseCurrency/, '') ===
                      this._route.snapshot.queryParamMap.get('metric')
              )
            : this.metricOptions[0]
    );

    activeMetricOption$ = this.metricCtrl.valueChanges.pipe(
        tap((metric) => {
            const queryParams = { metric: metric.value.replace(/InBaseCurrency/, '') };
            this._router.navigate([], {
                relativeTo: this._route,
                queryParams,
                queryParamsHandling: 'merge',
                replaceUrl: true
            });
        }),
        startWith(this.metricCtrl.value)
    );

    /** dimensionOptions
     * @description Dimensions for allocation view, i.e. what to group by
     */
    private readonly _dimensionOptions = [
        {
            label: 'Legal entity',
            value: 'legalEntityId',
            nameKey: 'legalEntity.shortName'
        },
        {
            label: 'Issuer',
            value: 'issuerId',
            nameKey: 'issuer.accountIssuerShortName',
            valueModifier: (id, entity) => {
                let override = null;
                if (entity.instrument?.assetClass === 'REAL_ASSET') {
                    override = 'NON_BANKABLE_ASSETS';
                } else if (entity.instrument?.assetClass === 'REAL_ESTATE') {
                    override = 'NON_BANKABLE_ASSETS';
                }
                return (
                    //override || entity.issuer?.accountIssuerId || 'NON_BANKABLE_ASSETS'
                    entity.issuer?.accountIssuerId || 'UNKNOWN'
                );
            },
            subgroup: {
                name: 'Account',
                key: 'accountId',
                nameKey: 'account.accountClientAlias',
                valueModifier: (id, entity) => {
                    let override = null;
                    if (entity.instrument?.assetClass === 'REAL_ASSET') {
                        override = 'NON_BANKABLE_ASSETS';
                    } else if (entity.instrument?.assetClass === 'REAL_ESTATE') {
                        override = 'NON_BANKABLE_ASSETS';
                    }
                    return override || id;
                }
            }
        },
        {
            label: 'Account',
            value: 'accountId',
            nameKey: 'account.accountClientAlias'
            /* uncomment to only group assets belonging to accounts to accounts, everything else to 'NON_BANKABLE_ASSETS'
           valueModifier(id, entity) {
               let override = null;
               if (entity.instrument?.assetClass === 'REAL_ASSET') {
                   override = 'NON_BANKABLE_ASSETS';
               } else if (entity.instrument?.assetClass === 'REAL_ESTATE') {
                   override = 'NON_BANKABLE_ASSETS';
               }
               return override || id;
           }
           */
        }
    ];

    dimensionOptions$ = combineQueries([
        of(this._dimensionOptions),
        //this._tagsQuery.asGroupMap$(),
        this._tagsQuery.hasLoaded$.pipe(
            switchMap((_) => this._tagsQuery.selectByGroupAndItemId$())
        )
    ]).pipe(
        map(([dimensions, tagGroups]) => {
            const tagDimensions = Object.keys(tagGroups).map((tagGroup) => {
                return {
                    label: capitalize(tagGroup),
                    value: tagGroup,
                    valueModifier: (id, entity) => {
                        const instrumentId = entity.instrument?.instrumentId;
                        const instrumentTag =
                            tagGroups[tagGroup].instrument[instrumentId];
                        /*if (!entity.instrument || !instrumentTag) {
                           return 'NOT_DEFINED';
                       }*/
                        const accountId = entity.accountId;
                        const accountTag = tagGroups[tagGroup]?.account[accountId];
                        /*
                        // TODO: use issuer from account
                        const issuerId = entity.account?.issuerId;
                        const issuerTag = this._tagsQuery.getByGroupAndIssuerId(
                            tagGroup,
                            issuerId
                        );*/
                        return instrumentTag?.title || accountTag?.title || 'NOT_TAGGED';
                    },
                    tagBased: true
                } as typeof this.dimensionCtrl.value;
            });
            return [...dimensions, ...tagDimensions];
        }),
        tap((options) => {
            const activeOption = this.dimensionCtrl.value;
            // if active option has no value modifier i.e. it's probably defined dynamically...
            if (!activeOption.valueModifier) {
                // ...find the option with the same value and set it as active once it has been dynamically defined
                const newActiveOption = options.find(
                    (option) => option.value === activeOption.value
                );
                // if the new active option is different from the current active option, set it as active
                if (
                    newActiveOption &&
                    JSON.stringify(newActiveOption) !== JSON.stringify(activeOption)
                ) {
                    this.dimensionCtrl.setValue(newActiveOption);
                }
            }
        })
    );

    dimensionCtrl = new FormControl<
        Partial<(typeof this._dimensionOptions)[0] & { tagBased: boolean }>
    >(
        this._route.snapshot.queryParamMap.get('groupMarketValueBy')
            ? this._dimensionOptions.find(
                  (option) =>
                      option.value ===
                      this._route.snapshot.queryParamMap.get('groupMarketValueBy')
              ) ?? {
                  label: this._route.snapshot.queryParamMap.get('groupMarketValueBy'),
                  value: this._route.snapshot.queryParamMap.get('groupMarketValueBy'),
                  valueModifier: null,
                  subgroup: null,
                  tagBased: !!this._route.snapshot.queryParamMap.get('groupMarketValueBy')
              }
            : this._dimensionOptions[0]
    );

    /** activeDimension$
     * @description Observable for active dimension, i.e. what dimension to view subgroups or entities for
     */
    activeDimension$ = defer(() => this.dimensionCtrl.valueChanges).pipe(
        distinctUntilChanged((a, b) => a.value === b.value),
        tap((dimension) => {
            const queryParams = {
                groupMarketValueBy: dimension.value
            };
            this._router.navigate([], {
                relativeTo: this._route,
                queryParams,
                queryParamsHandling: 'merge',
                replaceUrl: true
            });
        }),
        tap((dimension) => {
            // persist selection
            this.saveActiveDimension(dimension);
        }),
        tap((_) => {
            this.currentPage = 1;
        }),
        startWith(this.dimensionCtrl.value),
        map((dimension) => {
            return { ...dimension, tagBased: dimension['tagBased'] || false };
        }),
        shareReplay(1)
    );

    /** _groupedPositions$
     * @description Observable for grouped positions
     * @inputs _filteredPositions$, _accountsQuery.accountsAsObject$, _accountIssuersQuery.allAsObject$, _ownersQuery.allAsObject$, _instrumentsQuery.allAsObject$, activeDimension$, _settingsQuery.currenciesAndRates$
     */
    private _groupedPositions$ = combineQueries([
        this._positionsQuery.isLoading$.pipe(
            filter((isLoading) => !isLoading),
            tap((isLoading) => (this.isLoading = isLoading)),
            take(1)
        ),
        this._filteredPositions$,
        this._accountsQuery.nonBrokerAccountsAsObject$,
        this._accountIssuersQuery.allAsObject$,
        this._legalEntityQuery.allAsObject$,
        this._instrumentsQuery.allAsObject$,
        this.activeDimension$,
        this._settingsQuery.currenciesAndRates$,
        this.searchString$,
        this.maxAccountCloseDate$,
        this._tagsQuery.hasLoaded$
    ]).pipe(
        withLatestFrom(
            this._filteredAccountIds$,
            this.activeIssuerIds$,
            this.activeLegalEntityIds$
        ),
        map(
            ([
                [
                    positionsHasLoaded,
                    positions,
                    accountsHashMap,
                    issuersHashMap,
                    legalEntityHashMap,
                    instrumentsHashMap,
                    activeOption,
                    currenciesAndRates,
                    qs,
                    maxAccountCloseDate,
                    tagsHasLoaded
                ],
                filteredAccountIds,
                filteredIssuerIds,
                filteredLegalEntityIds
            ]) => {
                const fixedPositions: Array<ExtendedPosition> = [];
                positions.forEach((position) => {
                    const accountId = position.account.accountId;
                    const account: Acct = accountsHashMap[accountId];
                    const legalEntityId = legalEntityHashMap[account.ownerId].ownerId;
                    const legalEntity = legalEntityHashMap[legalEntityId];
                    const issuerId = account.accountIssuerId;
                    const issuer = issuersHashMap[issuerId];
                    let instrument = instrumentsHashMap[position.instrument.instrumentId];
                    let assetType = instrument?.assetType;

                    // determine asset type based on market value (applies to loans and cash)
                    if (!assetType) {
                        assetType =
                            position.marketValueInBaseCurrency > 0
                                ? 'ASSET'
                                : 'LIABILITY';
                    }
                    // determine instrument type for loans
                    if (instrument?.type === 'LOAN') {
                        if (assetType === 'ASSET') {
                            instrument = {
                                ...instrument,
                                instrumentType: 'LOANS_RECEIVABLE'
                            };
                        } else {
                            instrument = {
                                ...instrument,
                                instrumentType: 'LOANS_PAYABLE'
                            };
                        }
                    }

                    const assetClass =
                        instrument?.assetClass || 'ERROR_MISSING_INSTRUMENT';
                    const conversionRate = currenciesAndRates[position.position.currency];
                    const acquisitionValueInBaseCurrency =
                        position.acquisitionValue * conversionRate;
                    const totalPnlInBaseCurrency =
                        ((position.unrealizedPnLAvg || 0) +
                            (position.position.realizedPnLAvg || 0)) *
                        conversionRate;

                    // TODO: improve logic for searching
                    const searchString = (
                        ((instrument ? instrument['displayName'] : '') || '') +
                        '#' +
                        (instrument?.decoratedInstrumentName || '') +
                        '#' +
                        (account?.accountDisplayName || '') +
                        '#' +
                        (account?.accountClientAlias || '') +
                        '#' +
                        (account?.accountShortName || '') +
                        '#' +
                        (account?.accountLongName || '') +
                        '#' +
                        (account?.accountNumber || '') +
                        '#' +
                        (issuer?.displayName || '') +
                        '#' +
                        (issuer?.accountIssuerShortName || '') +
                        '#' +
                        (issuer?.accountIssuerLongName || '') +
                        '#' +
                        (legalEntity?.displayName || '') +
                        '#' +
                        (legalEntity?.shortName || '') +
                        '#' +
                        (legalEntity?.longName || '')
                    ).toLowerCase();

                    // if position matches search string or search string is empty...
                    if (
                        ((qs && searchString.indexOf(qs.toLowerCase()) >= 0) ||
                            qs === '') &&
                        !(
                            account.hideIfZeroBalance &&
                            (!position ||
                                Math.abs(position?.marketValueInBaseCurrency) <= 0.001)
                        ) &&
                        legalEntity?.status === 'ENABLED' &&
                        (!issuer || issuer?.status === 'ENABLED')
                    ) {
                        // ...add it to the list of positions
                        fixedPositions.push({
                            ...position,
                            acquisitionValueInBaseCurrency,
                            totalPnlInBaseCurrency,
                            issuer,
                            legalEntity,
                            legalEntityId,
                            issuerId,
                            accountId,
                            instrument,
                            assetType,
                            assetClass
                        });
                    }
                });
                let groupedPositions = accumulateByGroup<
                    (typeof fixedPositions)[0],
                    {
                        //name: string;
                        cashInBaseCurrency: number;
                        marketValueInBaseCurrency: number;
                        acquisitionValueInBaseCurrency: number;
                        totalPnlInBaseCurrency: number;
                    }
                >(
                    fixedPositions,
                    (position, acc, index, subgroup) => {
                        const cashInBaseCurrency =
                            (acc?.cashInBaseCurrency ?? 0) + position.cashInBaseCurrency;
                        const marketValueInBaseCurrency =
                            (acc?.marketValueInBaseCurrency ?? 0) +
                            position.marketValueInBaseCurrency;
                        const acquisitionValueInBaseCurrency =
                            (acc?.acquisitionValueInBaseCurrency ?? 0) +
                            position.acquisitionValueInBaseCurrency;
                        const totalPnlInBaseCurrency =
                            (acc?.totalPnlInBaseCurrency ?? 0) +
                            position.totalPnlInBaseCurrency;
                        return {
                            cashInBaseCurrency,
                            marketValueInBaseCurrency,
                            acquisitionValueInBaseCurrency,
                            totalPnlInBaseCurrency
                        };
                    },
                    {
                        //name: activeOption.label,
                        key: activeOption?.value,
                        valueModifier: activeOption?.valueModifier || null,
                        subgroup: activeOption?.subgroup || null
                    }
                );
                // declare accounts without positions
                const accountsWithoutPositions = Object.entries(accountsHashMap).reduce(
                    (acc, [accountId, account]) => {
                        // ...if account is part of the filtered accounts
                        const legalEntityId = account.ownerId;
                        const legalEntity = legalEntityHashMap[legalEntityId];
                        const issuerId = account.accountIssuerId;
                        const issuer = issuersHashMap[issuerId];
                        const groupedAccountIds = [
                            ...new Set(
                                Object.values(groupedPositions).reduce(
                                    (accountIds, accountId) => {
                                        return [
                                            ...accountIds,
                                            ...accountId.entities.map((a) => a.accountId)
                                        ];
                                    },
                                    []
                                )
                            )
                        ];
                        if (
                            account.status === 'ENABLED' &&
                            filteredAccountIds.indexOf(+accountId) >= 0 &&
                            groupedAccountIds.indexOf(+accountId) === -1 && // don't add accounts that are already in the grouped positions
                            qs === '' && // don't add accounts without positions if there is a search string
                            account.hideIfZeroBalance !== true &&
                            (account.closingDate
                                ? new Date(account.closingDate) > maxAccountCloseDate
                                : true) && // hide accounts that have been closed before the maxAccountCloseDate
                            legalEntity?.status === 'ENABLED' &&
                            (!issuer || issuer?.status === 'ENABLED')
                        ) {
                            acc[accountId] = {
                                cashInBaseCurrency: 0,
                                marketValueInBaseCurrency: 0,
                                acquisitionValueInBaseCurrency: 0,
                                totalPnlInBaseCurrency: 0,
                                entities: [account]
                            };
                        }
                        return acc;
                    },
                    {}
                );
                if (activeOption.value === 'accountId') {
                    groupedPositions = {
                        ...accountsWithoutPositions,
                        ...groupedPositions
                    };
                } else if (activeOption.value === 'issuerId') {
                    const issuersWithoutPositions = Object.keys(
                        accountsWithoutPositions
                    ).reduce((acc, accountId) => {
                        const account = accountsHashMap[accountId];
                        const issuerId = account.accountIssuerId || 'UNKNOWN';
                        if (!acc[issuerId]) {
                            acc[issuerId] = {
                                cashInBaseCurrency: 0,
                                marketValueInBaseCurrency: 0,
                                acquisitionValueInBaseCurrency: 0,
                                totalPnlInBaseCurrency: 0,
                                entities: [account]
                            };
                        } else {
                            acc[issuerId].entities.push(account);
                        }
                        return acc;
                    }, {});
                    groupedPositions = {
                        ...issuersWithoutPositions,
                        ...groupedPositions
                    };
                } else if (activeOption.value === 'legalEntityId') {
                    const legalEntitiesWithoutPositions = Object.keys(
                        accountsWithoutPositions
                    ).reduce((acc, accountId) => {
                        const account = accountsHashMap[accountId];
                        const legalEntityId = account.ownerId || 'UNKNOWN';
                        if (!acc[legalEntityId]) {
                            acc[legalEntityId] = {
                                cashInBaseCurrency: 0,
                                marketValueInBaseCurrency: 0,
                                acquisitionValueInBaseCurrency: 0,
                                totalPnlInBaseCurrency: 0,
                                entities: [account]
                            };
                        } else {
                            acc[legalEntityId].entities.push(account);
                        }
                        return acc;
                    }, {});
                    groupedPositions = {
                        ...legalEntitiesWithoutPositions,
                        ...groupedPositions
                    };
                } else if (activeOption.tagBased) {
                    const tagsWithoutPositions = Object.keys(
                        accountsWithoutPositions
                    ).reduce((acc, accountId) => {
                        const account = accountsHashMap[accountId];
                        const tag = this._tagsQuery.getByItemId(
                            accountId + '',
                            'account',
                            activeOption.value
                        )[0];
                        if (!!tag) {
                            if (!acc[tag.title]) {
                                acc[tag.title] = {
                                    cashInBaseCurrency: 0,
                                    marketValueInBaseCurrency: 0,
                                    acquisitionValueInBaseCurrency: 0,
                                    totalPnlInBaseCurrency: 0,
                                    entities: [account]
                                };
                            } else {
                                acc[tag.title].entities.push(account);
                            }
                        } else {
                            if (!acc['NOT_TAGGED']) {
                                acc['NOT_TAGGED'] = {
                                    cashInBaseCurrency: 0,
                                    marketValueInBaseCurrency: 0,
                                    acquisitionValueInBaseCurrency: 0,
                                    totalPnlInBaseCurrency: 0,
                                    entities: [account]
                                };
                            } else {
                                acc['NOT_TAGGED'].entities.push(account);
                            }
                        }
                        return acc;
                    }, {});
                    groupedPositions = {
                        ...tagsWithoutPositions,
                        ...groupedPositions
                    };
                }
                return { ...groupedPositions, entities: positions } as {
                    cashInBaseCurrency: number;
                    marketValueInBaseCurrency: number;
                    acquisitionValueInBaseCurrency: number;
                    totalPnlInBaseCurrency: number;
                    entities: Array<Position>;
                };
            }
        ),
        distinctUntilChanged((a, b) => JSON.stringify(a) === JSON.stringify(b)),
        shareReplay(1)
    );

    /** _sortedGroupedChunkedPositions$
     * @description Observable for grouped, sorted and chunked positions
     * @inputs _groupedPositions$, activeMetricOption$, activeLevel$, activeDimension$
     */
    private readonly _sortedGroupedChunkedPositions$ = combineQueries([
        this._groupedPositions$,
        this.activeMetricOption$,
        this.activeLevel$,
        this.activeDimension$,
        this.sorting$
    ]).pipe(
        withLatestFrom(this._settingsQuery.currenciesAndRates$),
        map(
            ([
                [groupedPositions, metric, levels, dimension, sorting],
                currenciesAndRates
            ]) => {
                let positions = { ...groupedPositions };
                const tagBased = dimension.tagBased;
                const sorted: Array<typeof positions & { name: string }> =
                    Object.entries<any>(positions || {})
                        .filter(([key, value]) => value[metric.key] !== undefined)
                        .map(([key, value]) => {
                            return {
                                id: key,
                                _id: `${dimension.value}_${key}`,
                                ...value
                            };
                        })
                        .sort((a, b) => {
                            // TODO: refactor this and simplify logic by adding name/title to the groups (can be used by the view too) no the logic is duplicated which is far from ideal
                            if (sorting.sortBy === 'default') {
                                if (tagBased) {
                                    const tagA = this._tagsQuery.getByTitle(a.id);
                                    const tagB = this._tagsQuery.getByTitle(b.id);
                                    if (tagA == null) {
                                        return 1;
                                    }
                                    if (tagB == null) {
                                        return -1;
                                    }
                                    return sorting.sortByOrder === 'asc'
                                        ? tagA?.order < tagB?.order
                                            ? -1
                                            : 1
                                        : tagA?.order < tagB?.order
                                        ? 1
                                        : -1;
                                } else if (dimension.value === 'accountId') {
                                    const A = this._accountsQuery.getEntity(a.id);
                                    const ADefault =
                                        A?.accountDisplayName ||
                                        A?.accountClientAlias ||
                                        A?.accountShortName;
                                    const B = this._accountsQuery.getEntity(b.id);
                                    const BDefault =
                                        B?.accountDisplayName ||
                                        B?.accountClientAlias ||
                                        B?.accountShortName;
                                    return sorting.sortByOrder === 'asc'
                                        ? ADefault.localeCompare(BDefault, 'en')
                                        : BDefault.localeCompare(ADefault, 'en');
                                } else if (dimension.value === 'legalEntityId') {
                                    const A = this._legalEntityQuery.getEntity(a.id);
                                    const ADefault = A?.shortName;
                                    const B = this._legalEntityQuery.getEntity(b.id);
                                    const BDefault = B?.shortName;
                                    return sorting.sortByOrder === 'asc'
                                        ? ADefault.localeCompare(BDefault, 'en')
                                        : BDefault.localeCompare(ADefault, 'en');
                                } else if (dimension.value === 'issuerId') {
                                    const A = this._accountIssuersQuery.getEntity(a.id);
                                    const ADefault =
                                        A?.displayName ||
                                        A?.accountIssuerShortName ||
                                        A?.accountIssuerLongName ||
                                        undefined;
                                    const B = this._accountIssuersQuery.getEntity(b.id);
                                    const BDefault =
                                        B?.displayName ||
                                        B?.accountIssuerShortName ||
                                        B?.accountIssuerLongName ||
                                        undefined;
                                    if (ADefault === undefined && BDefault === undefined)
                                        return 0;
                                    else if (BDefault === undefined) return -1;
                                    else if (ADefault === undefined) return 1;
                                    return sorting.sortByOrder === 'asc'
                                        ? ADefault.localeCompare(BDefault, 'en')
                                        : BDefault.localeCompare(ADefault, 'en');
                                }
                            } else if (sorting.sortByOrder === 'asc') {
                                return (
                                    get<string>(a, sorting.sortBy) + ''
                                ).localeCompare(
                                    get<string>(b, sorting.sortBy) + '',
                                    'en',
                                    { numeric: true }
                                );
                            } else {
                                return (
                                    get<string>(b, sorting.sortBy) + ''
                                ).localeCompare(
                                    get<string>(a, sorting.sortBy) + '',
                                    'en',
                                    { numeric: true }
                                );
                            }
                        });
                return sorted;
            }
        ),
        map((sorted) => {
            const maxSizePerBatch: number = 8;
            // TODO: move to utility function
            const chunk = <T>(array: Array<T>, size: number) =>
                Array.from({ length: Math.ceil(array.length / size) }, (value, index) =>
                    array.slice(index * size, index * size + size)
                ) as Array<Array<T>>;
            const data = chunk<(typeof sorted)[0]>(sorted, maxSizePerBatch);
            return { data, pages: data.length, total: sorted.length };
        }),
        shareReplay(1)
    );

    /** data$
     * @description Observable for data to display in the view
     * @inputs _sortedGroupedPositions$, _currentPage$
     */
    readonly data$ = combineQueries([
        this._sortedGroupedChunkedPositions$,
        this.currentPage$
    ]).pipe(
        map(([{ data, pages, total }, currentPage]) => {
            return {
                items: data[currentPage - 1],
                pages,
                total
            };
        }),
        shareReplay(1)
    );

    private _useShortNumberFormat: boolean = false;
    loadingBalance$ = new BehaviorSubject(true);

    onPageChange($event: PageEvent) {
        this.currentPage = $event.pageIndex + 1;
    }

    trackByFn(index, item) {
        return item._id;
    }

    settingsModalOptions?: ModalTemplateOptions;
    @ViewChild('settingsModalTemplate') set settingsTemplateRef(
        tmplRef: TemplateRef<any>
    ) {
        if (tmplRef) {
            this.settingsModalOptions = {
                modalTitle: 'Settings',
                closeButtonText: 'Hide settings',
                templateRef: tmplRef,
                allowTapClose: true,
                isStandalone: false,
                allowDragResize: true
            };
        }
    }
    toggleShowFilters() {
        this.showFilters = !this.showFilters;
    }

    toggleCurrencyDisplay() {
        this.balanceKey =
            this.balanceKey === 'balance' ? 'balanceInBaseCurrency' : 'balance';
    }

    ngOnDestroy() {
        this._unsubscribe$.next(true);
        this._unsubscribe$.complete();
    }

    toggleSortOrder() {
        const currentSortOrder = this.sortForm.controls.sortByOrder.value;
        this.sortForm.controls.sortByOrder.setValue(
            currentSortOrder === 'asc' ? 'desc' : 'asc'
        );
    }

    private _isStandalone = true;

    accountsMap$ = this._accountsQuery.accountsAsObject$;
    accountIssuersMap$ = this._accountIssuersQuery.allAsObject$;
    legalEntitiesMap$ = this._legalEntityQuery.allAsObject$;
    tagGroupsMap$ = this._tagsQuery.asGroupMap$({});

    filterTags($event: BadgesChangeEvent) {
        if ($event.type === 'toggle-active') {
            this.filterForm.controls.tags.setValue(
                $event.badges.filter((badge) => !badge.outline).map((badge) => badge.data)
            );
        }
    }

    /* TODO: remove once global filter for legal entities is implemented and released
    filterLegalEntities($event: BadgesChangeEvent<LegalEntity>) {
        if ($event.type === 'toggle-active') {
            this.legalEntitiesCtrl.setValue(
                $event.badges
                    .filter((badge) => !badge.outline)
                    .map((badge) => badge.data.ownerId)
            );
            const selected = $event.badges.filter((badge) => !badge.outline);
            const allSelected = selected.length === $event.badges.length;
            this.saveFilter({
                setting: 'LEGAL_ENTITY_FILTER',
                filter: allSelected ? [] : this.legalEntitiesCtrl.value
            });
        }
    }*/

    filterIssuers($event: BadgesChangeEvent<AccountIssuer>) {
        if ($event.type === 'toggle-active') {
            this.issuersCtrl.setValue(
                $event.badges
                    .filter((badge) => !badge.outline)
                    .map((badge) => badge.data.accountIssuerId)
            );
            const selected = $event.badges.filter((badge) => !badge.outline);
            const allSelected = selected.length === $event.badges.length;
            this.saveFilter({
                setting: 'ISSUER_FILTER',
                filter: allSelected ? [] : this.issuersCtrl.value
            });
        } else if ($event.type === 'not-allowed') {
            this._toastService.show({
                intent: 'info',
                message: $event.message,
                duration: 3000
            });
        }
    }

    filterInstruments($event: BadgesChangeEvent<Array<Instrument['instrumentId']>>) {
        if ($event.type === 'toggle-active') {
            this.instrumentsCtrl.setValue(
                $event.badges
                    .filter((badge) => !badge.outline)
                    .reduce((acc, badge) => {
                        return [...acc, ...badge.data];
                    }, [])
            );
            // check if all instruments are selected
            const selected = $event.badges.filter((badge) => !badge.outline);
            const allSelected = selected.length === $event.badges.length;
            this.saveFilter({
                setting: 'INSTRUMENT_FILTER',
                filter: allSelected ? [] : this.instrumentsCtrl.value
            });
        } else if ($event.type === 'not-allowed') {
            this._toastService.show({
                intent: 'info',
                message: $event.message,
                duration: 3000
            });
        }
    }

    saveFilter({
        setting,
        filter
    }: {
        setting: keyof ComponentSettings;
        filter:
            | Array<LegalEntity['ownerId']>
            | Array<Instrument['instrumentId']>
            | Array<Instrument['type']>
            | Array<Acct['accountType']>
            | Array<AccountIssuer['accountIssuerId']>;
    }) {
        this._userSettingsService
            .saveComponentSetting(setting, filter, 'sb-account-list', true)
            .subscribe();
    }

    /** saveActiveDimension
     * @description method to persist active dimension i.e. what the balance list is grouped on for user
     * @param dimension
     */
    saveActiveDimension(dimension: typeof this.dimensionCtrl.value) {
        this._userSettingsService
            .saveComponentSetting('ACTIVE_DIMENSION', dimension.value, 'sb-balance-list')
            .subscribe();
    }

    /** loadActiveDimension
     * @description method to load persisted dimension from user settings
     */
    loadActiveDimension() {
        this._userSettingsQuery
            .selectComponentSetting$('ACTIVE_DIMENSION', 'sb-balance-list')
            .pipe(
                filterNilValue(), // filter out actual persisted values (ignore null)
                distinctUntilChanged(), // only emit changes
                map((dimension) => {
                    // map value to actual option
                    return this._dimensionOptions.find(
                        (option) => option.value === dimension
                    );
                }),
                filterNilValue(), // ignore if option was not found for saved value
                take(1), // we only need the first real emission
                withLatestFrom(this.activeDimension$),
                takeUntil(this._unsubscribe$)
            )
            .subscribe(([dimension, currentDimension]) => {
                const routeDimension =
                    this._route.snapshot.queryParamMap.get('groupMarketValueBy');
                // if view is not initiated with a specific dimension passed in the url...
                if (!routeDimension && dimension.value !== currentDimension.value) {
                    // ...update dimension ctrl
                    this.dimensionCtrl.setValue(dimension);
                }
            });
    }

    private _hasPendingAnimation$ = new BehaviorSubject(false);
    hasPendingAnimation$ = this._hasPendingAnimation$.asObservable();
    private _isLoading$ = new BehaviorSubject(true);
    onAnimationStart($event: any) {
        this._hasPendingAnimation$.next(true);
    }

    onAnimationDone($event: any) {
        this._hasPendingAnimation$.next(false);
    }
}
